// 茶屋のたまき（猫又の女将・AI）と話す専用画面。#/support（screens/support.ts）。
// 会話劇（ADV）の形：茶屋の店先の背景にたまきの胸から上の絵（返事の種類で表情が変わる）、下に会話の窓、その下に入力欄。
// 種類は選ばせず、開くとたまきから話しかける。話ごとにスレッド（「相談」で一覧、「新しく」で別の話）。
// 運営へは取り次がない：購入・不具合などは、たまきが案内し、窓の下に「お問い合わせ」を開くボタンを出す。
// これまでのやりとりは「会話の記録」（スマホは開閉、PCは右に常に出す）。1画面に収め、流れるのは記録と窓の中だけ。
// 本文は textContent で入れ、HTML として解釈させない。チケットの判定はサーバー（worker の support/routes.ts）。
import { SUPPORT } from '@hyakki/core';
import { ApiError, supportApi, type SupportFace, type SupportMessage, type SupportQuota, type SupportThread } from '../api';
import { buildId, recentErrors } from '../error-log';
import { go } from '../router';
import { openContact } from './contact';
import { chatTime, esc } from './dom';
import { icon } from './icons';
import { LivePortrait, livePortrait } from './live-portrait';
import { openModal, toast } from './overlay';

const NAME = SUPPORT.name;
const MAX = 500;
const FACES: SupportFace[] = ['normal', 'smile', 'sorry', 'think'];

let threads: SupportThread[] = [];
let quota: SupportQuota | null = null;
let current: { thread: SupportThread; messages: SupportMessage[] } | null = null;
let busy = false;
let pendingText = '';
/** AIに答えてもらえない（チケット切れ・お休み）と分かった：チケットを買う・連携するを出す */
let noAi = false;
let unread = 0;
let onUnread = () => {};
let root: HTMLElement | null = null;
/** 動くたまき（WebGL が使えないとき・動きを減らす設定のときは静止画のまま） */
let live: LivePortrait | null = null;

export const supportUnread = () => unread;
export const setSupportUnreadListener = (fn: () => void) => (onUnread = fn);
const setUnread = (n: number) => {
  unread = n;
  onUnread();
};

const bust = (f: SupportFace) => `/art/support/okami-bust-${f}.webp`;
const faceIcon = (f: SupportFace = 'normal') => `/art/support/okami-${f}.webp`;

/** 一覧・残りの回数を取り直す（チャットを始めたとき・画面に戻ったとき）。未読の印にも使う */
export async function refreshSupport() {
  try {
    const r = await supportApi.list();
    threads = r.threads;
    quota = r.ai;
    setUnread(r.unread);
  } catch {
    /* 未ログイン・通信断は黙って続ける */
  }
}

/** 専用画面を描く（screens/support.ts の mount）。戻り値は後片付け */
export function mountSupport(el: HTMLElement): () => void {
  el.innerHTML = `<div class="page fill sup-page"><section class="sup-scene">
    <div class="sup-bg" aria-hidden="true"></div>
    <header class="sup-head">
      <button class="icon-btn" type="button" data-sup="back" aria-label="戻る">${icon('left')}</button>
      <h1 class="sup-title">茶屋</h1>
      <span class="sup-quota"></span>
      <button class="sup-hbtn" type="button" data-sup="log">${icon('history')}<span>記録</span></button>
      <button class="sup-hbtn" type="button" data-sup="history">${icon('scroll')}<span>相談</span></button>
      <button class="sup-hbtn" type="button" data-sup="new">${icon('plus')}<span>新しく</span></button>
    </header>
    <div class="sup-stage">${FACES.map((f) => `<img class="sup-bust" data-face="${f}" src="${bust(f)}" alt="" decoding="async">`).join('')}</div>
    <aside class="sup-log" aria-label="会話の記録"><ol class="sup-log-list"></ol></aside>
    <div class="sup-you" hidden><p></p></div>
    <div class="sup-boxwrap">
      <div class="sup-nameplate"><span class="sup-name"></span></div>
      <div class="sup-box" aria-live="polite">
        <div class="sup-text"></div>
        <div class="sup-typing" aria-label="入力中" hidden><i></i><i></i><i></i></div>
      </div>
    </div>
    <div class="sup-actions"></div>
    <form class="sup-form" autocomplete="off">
      <textarea class="sup-input" name="text" rows="1" maxlength="${MAX}" aria-label="${esc(SUPPORT.name)}に話す" enterkeyhint="enter"></textarea>
      <button class="btn primary sup-send" type="submit">送る</button>
    </form>
    <p class="sup-note">${esc(SUPPORT.name)}はAIです。誤った情報を伝えることがあります。</p>
  </section></div>`;
  root = el;
  const input = el.querySelector<HTMLTextAreaElement>('.sup-input')!;
  // Enter は改行（送るのは「送る」ボタンだけ。スマホのキーボードの確定・改行で誤って送らないように）。画面のショートカットには渡さない
  input.addEventListener('keydown', (e) => e.stopPropagation());
  input.addEventListener('input', () => fitInput(input));
  el.querySelector('form')!.addEventListener('submit', (e) => {
    e.preventDefault();
    void send();
  });
  const onClick = (e: Event) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-sup]');
    if (!b) return;
    const k = b.dataset.sup;
    if (k === 'back') history.length > 1 ? history.back() : go('top');
    else if (k === 'new') newThread();
    else if (k === 'history') historyDialog();
    else if (k === 'log') el.querySelector('.sup-scene')!.classList.toggle('log-open');
    else if (k === 'shop') go('shop', { cat: 'item' });
    else if (k === 'link') go('settings', { sec: 'account' });
    else if (k === 'contact') openContact('contact'); // 運営への連絡は、たまきを通さず「お問い合わせ・要望・報告」から
  };
  el.addEventListener('click', onClick);
  live = null;
  render();
  if (LivePortrait.available('okami'))
    void livePortrait('okami').then((lp) => {
      const stage = root === el ? el.querySelector<HTMLElement>('.sup-stage') : null;
      if (!lp || !stage) return;
      lp.canvas.className = 'sup-live';
      lp.attach(stage, NAME);
      live = lp;
      render();
    });
  void (async () => {
    await refreshSupport();
    // 続いている話があれば開く（いちばん新しい話）
    const id = current?.thread.id ?? threads.find((t) => t.status === 'ai')?.id;
    if (id) await openThread(id);
    else render();
  })();
  return () => {
    el.removeEventListener('click', onClick);
    root = null;
    live = null;
  };
}

function fitInput(t: HTMLTextAreaElement) {
  t.style.height = 'auto';
  t.style.height = `${Math.min(t.scrollHeight, 120)}px`;
}

async function openThread(id: string) {
  try {
    const r = await supportApi.thread(id);
    current = r;
    noAi = false;
    threads = threads.map((t) => (t.id === id ? r.thread : t));
    setUnread(threads.filter((t) => t.unread).length);
  } catch {
    toast('相談を開けませんでした。', 'error');
  }
  render();
}

function newThread() {
  current = null;
  noAi = false;
  greeting = greet();
  render();
  root?.querySelector<HTMLTextAreaElement>('.sup-input')?.focus();
}

/** 話しはじめのひとこと（時刻で替える。AIは呼ばない） */
function greet(): string {
  const h = new Date().getHours();
  const pick = (a: string[]) => a[Math.floor(Math.random() * a.length)];
  if (h >= 5 && h < 10) return pick(['おはようございます。朝のお茶、ちょうど淹れたところですよ。', 'あら、早いですね。今日はどこまで行くおつもり？']);
  if (h >= 10 && h < 17) return pick(['いらっしゃい。ひと休みしていきます？', 'ちょうどお団子が焼けたところなんです。座っていきません？']);
  if (h >= 17 && h < 22) return pick(['おかえりなさい。今日はどこまで行ってきました？', '日が暮れてきましたね。温かいお茶、いかが？']);
  return pick(['あら、こんな夜更けに。眠れないなら、少しお話ししましょうか。', '夜道はあやかしが多いですからね。少し休んでいきなさいな。']);
}
let greeting = greet();

function context() {
  return { screen: location.hash.slice(0, 200), ua: navigator.userAgent, viewport: `${innerWidth}x${innerHeight}`, dpr: devicePixelRatio, online: navigator.onLine, build: buildId(), errors: recentErrors() };
}

const ERR: Record<string, string> = {
  no_ticket: 'チャットチケットがありません。',
  free_paused: '今日の無料の分はおしまいです。チャットチケットなら話せます。',
  ai_off: `${SUPPORT.name}はいまお休みです。`,
  thread_long: '話が長くなったので、「新しく」から続きをどうぞ。',
  busy: '返事を待っています。',
  rate: '少し時間をおいてください。',
  closed: 'この話には書き足せません。「新しく」からどうぞ。',
  text_length: `${MAX}文字までです。`,
  text_lines: '改行が多すぎます。',
  text_chars: '使えない文字（見えない文字など）が含まれています。',
  text_empty: '内容を入力してください。',
};

async function send() {
  const input = root?.querySelector<HTMLTextAreaElement>('.sup-input');
  if (!input || busy) return;
  const text = input.value.trim();
  if (!text) return;
  busy = true;
  pendingText = text;
  render();
  try {
    const nonce = crypto.randomUUID?.() ?? String(Date.now());
    const r = current ? await supportApi.say(current.thread.id, text, nonce) : await supportApi.start(text, nonce, context());
    input.value = '';
    fitInput(input);
    current = { thread: r.thread, messages: r.messages };
    quota = r.ai;
    noAi = false;
    threads = [r.thread, ...threads.filter((t) => t.id !== r.thread.id)];
  } catch (e) {
    const code = e instanceof ApiError ? e.code : '';
    toast(ERR[code] ?? '送れませんでした。時間をおいてお試しください。', 'error', 4000);
    if (code === 'no_ticket' || code === 'free_paused' || code === 'ai_off') {
      noAi = true;
      void refreshSupport().then(render);
    }
  } finally {
    busy = false;
    pendingText = '';
    render();
  }
}

function historyDialog() {
  const md = openModal(
    threads.length
      ? `<ul class="sup-history">${threads
          .map(
            (t) => `<li><button type="button" class="sup-h-item" data-id="${esc(t.id)}">
              <span class="sup-h-title"></span>
              <small>${chatTime(t.updatedAt)}</small>${t.unread ? '<em class="badge">1</em>' : ''}</button></li>`,
          )
          .join('')}</ul>`
      : '<p class="muted">まだ話していません。</p>',
    { title: 'これまでの話' },
  );
  md.body.querySelectorAll<HTMLElement>('.sup-h-item').forEach((b, i) => (b.querySelector('.sup-h-title')!.textContent = threads[i].title));
  md.body.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-id]');
    if (!b) return;
    md.close();
    void openThread(b.dataset.id!);
  });
}

// ---------- 描画 ----------
type Line = { who: 'ai' | 'user' | 'staff' | 'system'; text: string; at?: number; face?: SupportFace };

/** 今の会話（記録に並べる行） */
function lines(): Line[] {
  const out: Line[] = [];
  if (current) for (const m of current.messages) out.push({ who: m.role, text: m.text, at: m.at, face: m.face });
  else out.push({ who: 'ai', text: greeting, face: 'smile' });
  if (pendingText) out.push({ who: 'user', text: pendingText });
  return out;
}

function render() {
  const el = root;
  if (!el) return;
  const q = el.querySelector<HTMLElement>('.sup-quota')!;
  q.textContent = quota ? `あと${(quota.freePaused ? 0 : quota.freeLeft) + quota.tickets}回` : '';
  const all = lines();
  // 窓に出す行：返事待ちなら文字の代わりに打っている点々、お知らせが最後ならそれ、ほかは最後のたまき（古い話は運営）の行
  const speaker = [...all].reverse().find((l) => l.who === 'ai' || l.who === 'staff');
  const sys = current ? [...current.messages].reverse().find((m) => m.role === 'system' && m.at >= (speaker?.at ?? 0)) : undefined;
  const shown: Line = busy ? { who: 'ai', text: '', face: 'normal' } : sys ? { who: 'system', text: sys.text } : (speaker ?? { who: 'ai', text: '', face: 'normal' });
  const face: SupportFace = shown.who === 'ai' ? (shown.face ?? 'normal') : shown.who === 'staff' ? 'normal' : 'think';
  if (live) live.setPose(face);
  else el.querySelectorAll<HTMLImageElement>('.sup-bust').forEach((img) => img.classList.toggle('on', img.dataset.face === face));
  el.querySelector('.sup-scene')!.classList.toggle('staff', shown.who === 'staff');
  const box = el.querySelector<HTMLElement>('.sup-box')!;
  box.classList.toggle('waiting', busy);
  el.querySelector('.sup-name')!.textContent = shown.who === 'staff' ? '運営' : shown.who === 'system' ? '' : NAME;
  box.querySelector('.sup-text')!.textContent = shown.text;
  box.querySelector<HTMLElement>('.sup-typing')!.hidden = !busy;
  // 最後の自分の発言（窓の上に小さく）
  const mine = [...all].reverse().find((l) => l.who === 'user');
  const lastIsMine = all[all.length - 1]?.who === 'user' || busy;
  const you = el.querySelector<HTMLElement>('.sup-you')!;
  you.hidden = !mine || !lastIsMine;
  you.querySelector('p')!.textContent = mine?.text ?? '';
  // 会話の記録
  const list = el.querySelector<HTMLOListElement>('.sup-log-list')!;
  list.innerHTML = all
    .map((l) =>
      l.who === 'system'
        ? '<li class="sys"><span></span></li>'
        : `<li class="${l.who}">${l.who === 'user' ? '' : l.who === 'ai' ? `<img src="${faceIcon(l.face)}" alt="">` : `<span class="sup-staff-ic">${icon('bell')}</span>`}<div><b></b><p></p>${l.at ? `<time>${chatTime(l.at)}</time>` : ''}</div></li>`,
    )
    .join('');
  list.querySelectorAll('li').forEach((li, i) => {
    const l = all[i];
    if (l.who === 'system') li.querySelector('span')!.textContent = l.text;
    else {
      li.querySelector('b')!.textContent = l.who === 'ai' ? NAME : l.who === 'staff' ? '運営' : 'あなた';
      li.querySelector('p')!.textContent = l.text;
    }
  });
  const log = el.querySelector<HTMLElement>('.sup-log')!;
  log.scrollTop = log.scrollHeight;
  renderFoot();
}

function renderFoot() {
  const el = root;
  if (!el) return;
  const acts = el.querySelector<HTMLElement>('.sup-actions')!;
  const form = el.querySelector<HTMLFormElement>('.sup-form')!;
  const input = form.querySelector<HTMLTextAreaElement>('textarea')!;
  const st = current?.thread.status;
  const outOfAi = noAi || !quota?.on || ((quota.freePaused || quota.freeLeft === 0) && quota.tickets === 0);
  const lastAi = current ? [...current.messages].reverse().find((m) => m.role !== 'user') : undefined;
  const b = (k: string, label: string, ic?: Parameters<typeof icon>[0]) => `<button class="btn small" type="button" data-sup="${k}">${ic ? icon(ic) : ''}${label}</button>`;
  let html = '';
  if (st && st !== 'ai') html = b('new', '新しく話す', 'plus');
  else if (outOfAi && quota?.on) html = quota.guest ? b('link', 'Google連携', 'google') : b('shop', 'チケット', 'shop');
  // たまきが「お問い合わせ・要望・報告」を案内したら、すぐ開けるように
  if (lastAi?.action === 'contact') html = b('contact', 'お問い合わせ', 'scroll') + html;
  acts.innerHTML = html;
  acts.hidden = !html || busy;
  const canType = !busy && (!st || st === 'ai');
  input.disabled = !canType;
  form.querySelector<HTMLButtonElement>('.sup-send')!.disabled = !canType || outOfAi;
  input.placeholder = !canType && st ? 'この話は終わっています' : outOfAi ? (quota && !quota.on ? `${SUPPORT.name}はお休み中です` : quota?.freePaused && quota.freeLeft > 0 ? '今日の無料の分はおしまいです' : 'チャットチケットがありません') : `${SUPPORT.name}に話す`;
}
